const TILE_PATTERNS = {
  robot: { wave: 'square', notes: [240, 360, 180, 420], step: 0.1 },
  animal: { wave: 'sawtooth', notes: [520, 720, 430], step: 0.14, glide: true },
  footsteps: { wave: 'sine', notes: [82, 74, 88], step: 0.19 },
  alarm: { wave: 'sawtooth', notes: [740, 520, 740, 520], step: 0.13 },
  magic: { wave: 'sine', notes: [523, 659, 784, 1047], step: 0.1 },
  splash: { noise: true, hits: [0, 0.12, 0.25], duration: 0.16 },
  boom: { wave: 'sine', notes: [92, 58], step: 0.12, glide: true, duration: 0.32 },
  laugh: { wave: 'square', notes: [330, 440, 330, 460], step: 0.1 },
  engine: { wave: 'sawtooth', notes: [90, 120, 165, 220], step: 0.11, glide: true },
  heartbeat: { wave: 'sine', notes: [72, 72, 72, 72], offsets: [0, 0.12, 0.4, 0.52] },
  countdown: { wave: 'sine', notes: [660, 660, 880], step: 0.18 },
  applause: { noise: true, hits: [0, 0.08, 0.17, 0.26, 0.34], duration: 0.08 },
  thunder: { noise: true, hits: [0, 0.08], duration: 0.38 },
  whisper: { noise: true, hits: [0], duration: 0.48, quiet: true },
  crunch: { noise: true, hits: [0, 0.09, 0.2], duration: 0.07 },
  bubble: { wave: 'sine', notes: [240, 340, 460, 620], step: 0.09, glide: true },
  phone: { wave: 'sine', notes: [660, 880, 660, 880], step: 0.13 },
  train: { wave: 'triangle', notes: [110, 110, 147, 110], step: 0.13 },
  ghost: { wave: 'sine', notes: [420, 330, 250], step: 0.16, glide: true },
  music: { wave: 'triangle', notes: [262, 330, 392, 523], step: 0.1 },
  error: { wave: 'square', notes: [170, 120, 170], step: 0.13 },
  rocket: { wave: 'sawtooth', notes: [80, 130, 220, 360], step: 0.11, glide: true },
  door: { wave: 'sawtooth', notes: [180, 145, 110], step: 0.14, glide: true },
  surprise: { wave: 'sine', notes: [392, 523, 659, 1047], step: 0.08 },
};

class PartyAudio {
  constructor() {
    this.context = null;
    this.enabled = localStorage.getItem('joybox-party-sound') !== 'off';
    this.active = new Set();
  }

  async unlock() {
    if (!this.context) this.context = new AudioContext();
    if (this.context.state === 'suspended') await this.context.resume();
    this.enabled = true;
    localStorage.setItem('joybox-party-sound', 'on');
  }

  toggle() {
    this.enabled = !this.enabled;
    localStorage.setItem('joybox-party-sound', this.enabled ? 'on' : 'off');
    if (!this.enabled) this.stop();
    else this.unlock().catch(() => {});
    return this.enabled;
  }

  async playSignal(clue, preview = false) {
    if (!this.enabled || !clue) return;
    await this.unlock();
    this.stop();
    const start = this.context.currentTime + 0.04;
    const tiles = Array.isArray(clue.tiles) ? clue.tiles : [];
    tiles.forEach((tileId, tileIndex) => this.playSignalTile(tileId, start + tileIndex * 0.78));
    if (!preview && navigator.vibrate) navigator.vibrate(tiles.flatMap(() => [35, 80]));
  }

  playSignalTile(tileId, start) {
    const pattern = TILE_PATTERNS[tileId] || TILE_PATTERNS.robot;
    if (pattern.noise) {
      for (const offset of pattern.hits) this.playNoise(start + offset, pattern.duration, pattern.quiet ? 0.025 : 0.07);
      return;
    }
    const offsets = pattern.offsets || pattern.notes.map((_, index) => index * pattern.step);
    pattern.notes.forEach((note, index) => {
      const oscillator = this.context.createOscillator();
      const gain = this.context.createGain();
      const offset = offsets[index];
      const duration = pattern.duration || 0.2;
      oscillator.type = pattern.wave;
      oscillator.frequency.setValueAtTime(note, start + offset);
      if (pattern.glide) oscillator.frequency.exponentialRampToValueAtTime(Math.max(40, note * 1.22), start + offset + duration);
      gain.gain.setValueAtTime(0.0001, start + offset);
      gain.gain.exponentialRampToValueAtTime(tileId === 'footsteps' || tileId === 'boom' ? 0.13 : 0.065, start + offset + 0.014);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + duration);
      oscillator.connect(gain).connect(this.context.destination);
      oscillator.start(start + offset);
      oscillator.stop(start + offset + duration + 0.02);
      this.active.add(oscillator);
      oscillator.onended = () => this.active.delete(oscillator);
    });
  }

  playNoise(start, duration, volume) {
    const length = Math.max(1, Math.floor(this.context.sampleRate * duration));
    const buffer = this.context.createBuffer(1, length, this.context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < length; index += 1) data[index] = (Math.random() * 2 - 1) * (1 - index / length);
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    source.buffer = buffer;
    filter.type = 'lowpass';
    filter.frequency.value = duration > 0.3 ? 420 : 1800;
    gain.gain.setValueAtTime(volume, start);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    source.connect(filter).connect(gain).connect(this.context.destination);
    source.start(start);
    source.stop(start + duration);
    this.active.add(source);
    source.onended = () => this.active.delete(source);
  }

  playCue(kind = 'tap') {
    if (!this.enabled) return;
    this.unlock().then(() => {
      const map = {
        tap: [440, 0.06],
        correct: [660, 0.11],
        reveal: [330, 0.16],
        win: [523, 0.22],
      };
      const [frequency, duration] = map[kind] || map.tap;
      const oscillator = this.context.createOscillator();
      const gain = this.context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.05, this.context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, this.context.currentTime + duration);
      oscillator.connect(gain).connect(this.context.destination);
      oscillator.start();
      oscillator.stop(this.context.currentTime + duration);
    }).catch(() => {});
  }

  playEffect(kind) {
    if (!this.enabled) return;
    this.unlock().then(() => {
      const effects = {
        launch: { wave: 'sawtooth', notes: [110, 165, 247, 370], step: 0.1, duration: 0.24 },
        vault: { wave: 'sine', notes: [392, 523, 659, 784], step: 0.12, duration: 0.32 },
        drift: { wave: 'triangle', notes: [330, 294, 247], step: 0.15, duration: 0.28 },
        current: { wave: 'sine', notes: [262, 330, 392, 523, 659], step: 0.07, duration: 0.22 },
      };
      const effect = effects[kind] || effects.current;
      const start = this.context.currentTime + 0.025;
      effect.notes.forEach((frequency, index) => {
        const oscillator = this.context.createOscillator();
        const gain = this.context.createGain();
        oscillator.type = effect.wave;
        oscillator.frequency.setValueAtTime(frequency, start + index * effect.step);
        gain.gain.setValueAtTime(0.0001, start + index * effect.step);
        gain.gain.exponentialRampToValueAtTime(0.045, start + index * effect.step + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + index * effect.step + effect.duration);
        oscillator.connect(gain).connect(this.context.destination);
        oscillator.start(start + index * effect.step);
        oscillator.stop(start + index * effect.step + effect.duration + 0.02);
        this.active.add(oscillator);
        oscillator.onended = () => this.active.delete(oscillator);
      });
      if (kind === 'launch' && navigator.vibrate) navigator.vibrate([35, 30, 55]);
    }).catch(() => {});
  }

  speak(text) {
    if (!this.enabled || !('speechSynthesis' in window)) return;
    speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.93;
    utterance.pitch = 1.05;
    utterance.volume = 0.9;
    speechSynthesis.speak(utterance);
  }

  stop() {
    for (const source of this.active) {
      try { source.stop(); } catch { /* already ended */ }
    }
    this.active.clear();
    if ('speechSynthesis' in window) speechSynthesis.cancel();
  }
}

export const audio = new PartyAudio();
